<div class="absolute inset-0 flex min-w-0 flex-col overflow-auto">
    <!-- Main -->
    <div class="flex-1 flex flex-col">
        <!-- Header -->
        <div class="bg-default flex flex-col items-start px-4 py-6 sm:px-6 sm:py-8 sm:flex-row sm:items-center sm:justify-between flex-shrink-0">
            <!-- Title -->
            <div>
                <div class="text-3xl sm:text-4xl font-extrabold leading-none tracking-tight">Support Tickets</div>
                <div class="text-secondary ml-0.5 font-medium text-sm sm:text-base">
                    <span>Manage and respond to user support requests</span>
                </div>
            </div>
            
            <!-- Search and Actions -->
            <div class="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <!-- Search Bar -->
                <div class="relative w-full sm:w-64">
                    <div class="relative">
                        <mat-icon class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">search</mat-icon>
                        <input type="text" 
                               [(ngModel)]="searchQuery"
                               (ngModelChange)="onSearchInput()"
                               (focus)="showSearchDropdown = filteredTickets.length > 0 && searchQuery.trim() !== ''"
                               (blur)="onSearchBlur()"
                               placeholder="Search by email, comments, or status..."
                               class="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-sm">
                        
                        <!-- Clear button -->
                        <button *ngIf="searchQuery" 
                                (click)="clearSearch()"
                                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <mat-icon class="text-sm">close</mat-icon>
                        </button>
                    </div>
                    
                    <!-- Search Dropdown -->
                    <div *ngIf="showSearchDropdown && filteredTickets.length > 0" 
                         class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                        <div class="p-2">
                            <div class="text-xs font-semibold text-gray-500 px-3 py-2">Search Results ({{filteredTickets.length}})</div>
                            <div class="divide-y divide-gray-100">
                                <div *ngFor="let ticket of filteredTickets" 
                                     (click)="selectTicket(ticket)"
                                     class="px-3 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150">
                                    <div class="flex items-center">
                                        <div class="w-8 h-8 rounded-full flex items-center justify-center mr-3 overflow-hidden bg-blue-500 flex-shrink-0">
                                            <span class="text-white text-xs font-bold">
                                                {{ticket.email.charAt(0).toUpperCase()}}
                                            </span>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <div class="font-medium text-gray-900 truncate text-sm">{{ticket.email}}</div>
                                            <div class="text-xs text-gray-500 truncate" *ngIf="ticket.comments">{{ticket.comments | slice:0:40}}{{ticket.comments.length > 40 ? '...' : ''}}</div>
                                            <div class="text-xs text-gray-500 truncate" *ngIf="!ticket.comments">No comments</div>
                                            <div class="text-xs text-gray-400 mt-1">
                                                <span [ngClass]="getStatusClass(ticket.status)" 
                                                      class="inline-flex items-center px-2 py-0.5 rounded">
                                                    {{ticket.status | titlecase}}
                                                </span>
                                                <span class="mx-1">•</span>
                                                <span class="text-xs">{{formatDateShort(ticket.createdAt)}}</span>
                                            </div>
                                        </div>
                                        <mat-icon class="text-gray-400 ml-2 flex-shrink-0">chevron_right</mat-icon>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Selected Ticket Badge -->
                    <div *ngIf="selectedTicket" class="mt-2">
                        <div class="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs">
                            <div class="w-4 h-4 rounded-full mr-1 flex items-center justify-center overflow-hidden bg-blue-500 flex-shrink-0">
                                <span class="text-white text-xs font-bold">
                                    {{selectedTicket.email.charAt(0).toUpperCase()}}
                                </span>
                            </div>
                            <span class="font-medium truncate">{{selectedTicket.email}}</span>
                            <span class="mx-2">•</span>
                            <span [ngClass]="getStatusClass(selectedTicket.status)" 
                                  class="text-xs px-2 py-0.5 rounded truncate">
                                {{selectedTicket.status | titlecase}}
                            </span>
                            <button (click)="clearSearch()" class="ml-2 text-blue-500 hover:text-blue-700 flex-shrink-0">
                                <mat-icon class="text-xs">close</mat-icon>
                            </button>
                        </div>
                    </div>
                </div>
                
                <!-- Refresh Button -->
                <button mat-button 
                        (click)="refreshTickets()"
                        class="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 text-sm">
                    <mat-icon class="mr-2 text-sm">refresh</mat-icon>
                    Refresh
                </button>
            </div>
        </div>

        <!-- Filters -->
        <div class="px-4 sm:px-6 py-4 sm:py-6">
            <form [formGroup]="filterForm" class="space-y-4">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <!-- Email Filter -->
                    <mat-form-field class="w-full">
                        <mat-label>Email</mat-label>
                        <mat-select formControlName="email">
                            <mat-option value="">All Emails</mat-option>
                            <mat-option *ngFor="let email of userEmails" [value]="email">
                                {{ email }}
                            </mat-option>
                        </mat-select>
                        <mat-icon matPrefix class="text-sm">email</mat-icon>
                    </mat-form-field>

                    <!-- Status Filter -->
                    <mat-form-field class="w-full">
                        <mat-label>Status</mat-label>
                        <mat-select formControlName="status">
                            <mat-option *ngFor="let status of statusOptions" [value]="status.value">
                                {{ status.label }}
                            </mat-option>
                        </mat-select>
                        <mat-icon matPrefix class="text-sm">flag</mat-icon>
                    </mat-form-field>
                </div>

                <!-- Filter Actions -->
                <div class="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                        type="button"
                        mat-raised-button
                        color="primary"
                        (click)="applyFilters()"
                        [disabled]="isFilterLoading || isLoading"
                        class="inline-flex items-center justify-center px-4 py-2 text-sm">
                        <div class="flex items-center gap-2">
                            <mat-spinner diameter="16" *ngIf="isFilterLoading"></mat-spinner>
                            <mat-icon class="text-sm">search</mat-icon>
                            <span>{{ isFilterLoading ? 'Applying...' : 'Apply Filters' }}</span>
                        </div>
                    </button>

                    <button
                        type="button"
                        mat-stroked-button
                        (click)="clearFilter()"
                        [disabled]="isFilterLoading || isLoading"
                        class="inline-flex items-center justify-center px-4 py-2 text-sm">
                        <div class="flex items-center gap-2">
                            <mat-icon class="text-sm">clear</mat-icon>
                            <span>Clear Filters</span>
                        </div>
                    </button>
                </div>
            </form>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoading" class="flex-1 flex items-center justify-center">
            <div class="text-center">
                <mat-spinner diameter="50"></mat-spinner>
                <p class="mt-4 text-gray-600">Loading tickets...</p>
            </div>
        </div>

        <!-- Tickets Table -->
        <div *ngIf="!isLoading" class="px-4 sm:px-6 pb-6 flex-1 overflow-auto">
            <!-- Search Info -->
            <div *ngIf="isSearchActive" class="mb-4">
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                        <div class="flex items-center">
                            <mat-icon class="text-blue-600 mr-2 text-sm">info</mat-icon>
                            <span class="text-blue-700 text-sm">
                                Showing search results for "{{searchQuery}}"
                            </span>
                        </div>
                        <button mat-button 
                                (click)="clearSearch()"
                                class="text-blue-600 hover:text-blue-800 text-sm">
                            Clear Search
                        </button>
                    </div>
                </div>
            </div>

            <!-- Summary Stats -->
            <div *ngIf="!isSearchActive && displayedTickets.length > 0" class="mb-4 sm:mb-6">
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                    <div class="px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                        <div class="text-xs text-blue-600">Total Tickets</div>
                        <div class="text-base sm:text-lg font-semibold text-blue-800">{{ totalItems }}</div>
                    </div>
                    <div class="px-3 py-2 bg-red-50 rounded-lg border border-red-200">
                        <div class="text-xs text-red-600">Open</div>
                        <div class="text-base sm:text-lg font-semibold text-red-800">{{ getOpenTicketCount() }}</div>
                    </div>
                    <div class="px-3 py-2 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div class="text-xs text-yellow-600">In Progress</div>
                        <div class="text-base sm:text-lg font-semibold text-yellow-800">{{ getInProgressTicketCount() }}</div>
                    </div>
                    <div class="px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                        <div class="text-xs text-green-600">Closed</div>
                        <div class="text-base sm:text-lg font-semibold text-green-800">{{ getClosedTicketCount() }}</div>
                    </div>
                </div>
            </div>
            
            <!-- Tickets Table -->
            <div *ngIf="displayedTickets.length > 0" class="w-full">
                <div class="bg-white rounded-lg shadow overflow-hidden w-full">
                    <!-- Table Container - No horizontal scrolling -->
                    <div class="w-full overflow-hidden">
                        <div class="min-w-full">
                            <!-- Mobile Card View -->
                            <div class="sm:hidden">
                                <div class="space-y-3 p-3">
                                    <ng-container *ngFor="let ticket of displayedTickets | slice:currentPage*pageSize:(currentPage+1)*pageSize; trackBy: trackByFn">
                                        <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                                            <div class="flex items-start justify-between mb-3">
                                                <div class="flex items-center flex-1">
                                                    <div class="w-8 h-8 rounded-full overflow-hidden bg-blue-500 flex-shrink-0">
                                                        <span class="h-full w-full flex items-center justify-center text-white text-xs font-bold">
                                                            {{ticket.email.charAt(0).toUpperCase()}}
                                                        </span>
                                                    </div>
                                                    <div class="ml-3 flex-1 min-w-0">
                                                        <div class="text-sm font-medium text-gray-900 truncate">{{ticket.email}}</div>
                                                        <div class="text-xs text-gray-500">ID: {{ticket._id.substring(0, 8)}}</div>
                                                    </div>
                                                </div>
                                                <button mat-icon-button 
                                                        class="text-blue-600 hover:bg-blue-50 flex-shrink-0 ml-2"
                                                        (click)="toggleRow(ticket._id, $event)">
                                                    <mat-icon class="text-sm">{{isTicketExpanded(ticket._id) ? 'expand_less' : 'expand_more'}}</mat-icon>
                                                </button>
                                            </div>
                                            
                                            <div class="space-y-2 mb-3">
                                                <div class="text-sm text-gray-700 line-clamp-2">{{ticket.comments}}</div>
                                                
                                                <div class="flex items-center justify-between">
                                                    <span class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
                                                          [ngClass]="getStatusClass(ticket.status)">
                                                        <mat-icon class="mr-1 text-xs">{{getStatusIcon(ticket.status)}}</mat-icon>
                                                        {{ticket.status | titlecase}}
                                                    </span>
                                                    
                                                    <mat-form-field class="w-28">
                                                        <mat-select [value]="ticket.status"
                                                                   (selectionChange)="updateTicketStatus(ticket._id, $event.value)"
                                                                   class="text-xs">
                                                            <mat-option value="open">Open</mat-option>
                                                            <mat-option value="in-progress">In Progress</mat-option>
                                                            <mat-option value="closed">Closed</mat-option>
                                                        </mat-select>
                                                    </mat-form-field>
                                                </div>
                                            </div>
                                            
                                            <div class="flex items-center justify-between text-xs text-gray-500 border-t pt-2">
                                                <div>
                                                    <div>{{formatDateShort(ticket.createdAt)}}</div>
                                                    <div>{{ getTimeFromDate(ticket.createdAt) }}</div>
                                                </div>
                                                <button *ngIf="ticket.document"
                                                        mat-stroked-button
                                                        color="primary"
                                                        (click)="openDocument(ticket.document)"
                                                        class="text-xs">
                                                    <mat-icon class="text-xs mr-1">attach_file</mat-icon>
                                                    View
                                                </button>
                                            </div>

                                            <!-- Expanded Mobile Details -->
                                            <div *ngIf="isTicketExpanded(ticket._id)" class="mt-4 pt-4 border-t border-gray-200">
                                                <div class="space-y-3">
                                                    <div>
                                                        <div class="text-xs font-medium text-gray-600 mb-1">Email:</div>
                                                        <a [href]="'mailto:' + ticket.email" 
                                                           class="text-blue-600 hover:text-blue-800 text-sm">
                                                            {{ticket.email}}
                                                        </a>
                                                    </div>
                                                    <div>
                                                        <div class="text-xs font-medium text-gray-600 mb-1">Comments:</div>
                                                        <p class="text-sm text-gray-900">{{ticket.comments}}</p>
                                                    </div>
                                                    <div class="grid grid-cols-2 gap-3 text-xs text-gray-500">
                                                        <div>
                                                            <div class="font-medium">Created:</div>
                                                            <div>{{formatDate(ticket.createdAt)}}</div>
                                                        </div>
                                                        <div>
                                                            <div class="font-medium">Updated:</div>
                                                            <div>{{formatDate(ticket.updatedAt)}}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </ng-container>
                                </div>
                            </div>

                            <!-- Desktop Table View -->
                            <div class="hidden sm:block">
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead class="bg-gray-50">
                                        <tr>
                                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Email
                                            </th>
                                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Comments
                                            </th>
                                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Attachment
                                            </th>
                                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody class="bg-white divide-y divide-gray-200">
                                        <ng-container *ngFor="let ticket of displayedTickets | slice:currentPage*pageSize:(currentPage+1)*pageSize; trackBy: trackByFn">
                                            <!-- Main Row -->
                                            <tr id="ticket-{{ticket._id}}" class="hover:bg-gray-50">
                                                <!-- Email Column -->
                                                <td class="px-4 py-3">
                                                    <div class="flex items-center">
                                                        <div class="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-blue-500">
                                                            <span class="h-full w-full flex items-center justify-center text-white text-xs font-bold">
                                                                {{ticket.email.charAt(0).toUpperCase()}}
                                                            </span>
                                                        </div>
                                                        <div class="ml-3 min-w-0">
                                                            <div class="text-sm font-medium text-gray-900 truncate max-w-[150px]">{{ticket.email}}</div>
                                                            <div class="text-xs text-gray-500 truncate max-w-[150px]">ID: {{ticket._id.substring(0, 8)}}</div>
                                                        </div>
                                                    </div>
                                                </td>

                                                <!-- Comments Column -->
                                                <td class="px-4 py-3 max-w-[200px]">
                                                    <div class="text-sm text-gray-900 truncate" [matTooltip]="ticket.comments">
                                                        {{ticket.comments | slice:0:60}}{{ticket.comments.length > 60 ? '...' : ''}}
                                                    </div>
                                                </td>

                                                <!-- Attachment Column -->
                                                <td class="px-4 py-3">
                                                    <button *ngIf="ticket.document"
                                                            mat-stroked-button
                                                            color="primary"
                                                            (click)="openDocument(ticket.document)"
                                                            class="text-xs inline-flex items-center">
                                                        <mat-icon class="text-xs mr-1">attach_file</mat-icon>
                                                        View
                                                    </button>
                                                    <span *ngIf="!ticket.document" class="text-gray-500 italic text-xs">None</span>
                                                </td>

                                                <!-- Status Column -->
                                                <td class="px-4 py-3">
                                                    <span class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
                                                          [ngClass]="getStatusClass(ticket.status)">
                                                        <mat-icon class="mr-1 text-xs">{{getStatusIcon(ticket.status)}}</mat-icon>
                                                        {{ticket.status | titlecase}}
                                                    </span>
                                                </td>

                                                <!-- Date Column -->
                                                <td class="px-4 py-3">
                                                    <div class="text-sm text-gray-900">{{formatDateShort(ticket.createdAt)}}</div>
                                                    <div class="text-xs text-gray-500">
                                                        {{ getTimeFromDate(ticket.createdAt) }}
                                                    </div>
                                                </td>

                                                <!-- Actions Column -->
                                                <td class="px-4 py-3">
                                                    <div class="flex items-center space-x-2">
                                                        <!-- Status Update -->
                                                        <mat-form-field class="w-28">
                                                            <mat-select [value]="ticket.status"
                                                                       (selectionChange)="updateTicketStatus(ticket._id, $event.value)"
                                                                       class="text-xs">
                                                                <mat-option value="open">Open</mat-option>
                                                                <mat-option value="in-progress">In Progress</mat-option>
                                                                <mat-option value="closed">Closed</mat-option>
                                                            </mat-select>
                                                        </mat-form-field>
                                                        
                                                        <!-- Expand Button -->
                                                        <button mat-icon-button 
                                                                matTooltip="View Details"
                                                                class="text-blue-600 hover:bg-blue-50"
                                                                (click)="toggleRow(ticket._id, $event)">
                                                            <mat-icon class="text-sm">{{isTicketExpanded(ticket._id) ? 'expand_less' : 'expand_more'}}</mat-icon>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            <!-- Expanded Details Row -->
                                            <tr *ngIf="isTicketExpanded(ticket._id)" class="bg-blue-50">
                                                <td colspan="6" class="px-4 py-4">
                                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <!-- Ticket Details -->
                                                        <div class="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                                            <h3 class="text-md font-semibold text-gray-900 mb-3 flex items-center">
                                                                <mat-icon class="mr-2 text-blue-600 text-sm">support_agent</mat-icon>
                                                                Ticket Details
                                                            </h3>
                                                            <div class="space-y-2">
                                                                <div class="flex items-center">
                                                                    <mat-icon class="mr-2 text-gray-400 text-sm">email</mat-icon>
                                                                    <span class="text-sm font-medium text-gray-600 mr-2">Email:</span>
                                                                    <a [href]="'mailto:' + ticket.email" 
                                                                       class="text-blue-600 hover:text-blue-800 hover:underline text-sm truncate">
                                                                        {{ticket.email}}
                                                                    </a>
                                                                </div>
                                                                <div class="flex items-center">
                                                                    <mat-icon class="mr-2 text-gray-400 text-sm">verified</mat-icon>
                                                                    <span class="text-sm font-medium text-gray-600 mr-2">Ticket ID:</span>
                                                                    <span class="text-sm font-mono truncate">{{ticket._id}}</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <!-- Additional Information -->
                                                        <div class="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                                            <h3 class="text-md font-semibold text-gray-900 mb-3 flex items-center">
                                                                <mat-icon class="mr-2 text-green-600 text-sm">info</mat-icon>
                                                                Additional Information
                                                            </h3>
                                                            <div class="space-y-2">
                                                                <!-- Comments -->
                                                                <div>
                                                                    <div class="text-xs font-medium text-gray-600 mb-1">Comments:</div>
                                                                    <p class="text-sm text-gray-900">{{ticket.comments}}</p>
                                                                </div>

                                                                <!-- Attachment -->
                                                                <div>
                                                                    <div class="text-xs font-medium text-gray-600 mb-1">Attachment:</div>
                                                                    <div>
                                                                        <button *ngIf="ticket.document"
                                                                                mat-stroked-button
                                                                                color="primary"
                                                                                (click)="openDocument(ticket.document)"
                                                                                class="text-xs">
                                                                            <mat-icon class="text-xs mr-1">attach_file</mat-icon>
                                                                            View Attachment
                                                                        </button>
                                                                        <span *ngIf="!ticket.document" class="text-gray-500 italic text-xs">No attachment</span>
                                                                    </div>
                                                                </div>

                                                                <!-- Dates -->
                                                                <div class="pt-2 border-t border-gray-100">
                                                                    <div class="grid grid-cols-2 gap-2 text-xs text-gray-500">
                                                                        <div>
                                                                            <div class="font-medium">Created:</div>
                                                                            <div>{{formatDate(ticket.createdAt)}}</div>
                                                                        </div>
                                                                        <div>
                                                                            <div class="font-medium">Updated:</div>
                                                                            <div>{{formatDate(ticket.updatedAt)}}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </ng-container>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Table Footer with Pagination -->
                    <div class="bg-gray-50 px-4 py-3 border-t border-gray-200">
                        <div class="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
                            <div class="text-sm text-gray-700">
                                Showing <span class="font-medium">{{displayedTickets.length}}</span> of 
                                <span class="font-medium">{{totalItems}}</span> tickets
                                <span *ngIf="isSearchActive" class="text-blue-600 ml-2">
                                    (Search Results)
                                </span>
                            </div>
                            
                            <mat-paginator [length]="totalItems"
                                           [pageSize]="pageSize"
                                           [pageSizeOptions]="pageSizeOptions"
                                           [pageIndex]="currentPage"
                                           (page)="onPageChange($event)"
                                           aria-label="Select page"
                                           class="border-0">
                            </mat-paginator>
                            
                            <button mat-button 
                                    (click)="refreshTickets()"
                                    class="text-blue-600 hover:text-blue-800 text-sm">
                                <mat-icon class="text-sm">refresh</mat-icon>
                                <span class="ml-1">Refresh</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div *ngIf="displayedTickets.length === 0 && !isLoading" class="text-center py-12">
                <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <mat-icon class="text-gray-400" style="width: 32px; height: 32px; font-size: 32px;">support_agent</mat-icon>
                </div>
                <h3 class="text-lg font-medium text-gray-900 mb-2">
                    {{ isSearchActive ? 'No matching tickets found' : 'No tickets found' }}
                </h3>
                <p class="text-gray-500 mb-4">
                    {{ isSearchActive ? 'Try a different search term' : 'There are no support tickets in the system.' }}
                </p>
                <button *ngIf="isSearchActive" 
                        mat-button 
                        (click)="clearSearch()"
                        class="text-blue-600 hover:text-blue-800">
                    Clear Search
                </button>
            </div>
        </div>
    </div>
</div>